import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';

export default function AdminPaymentPage() {
  const api = useApi();
  const [config, setConfig] = useState({
    entryFee: '', paypalUrl: '', venmoUsername: '', contactEmail: '', enabled: false, hideUnpaid: false,
  });
  const [brackets, setBrackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterPaid, setFilterPaid] = useState('all');
  const [togglingId, setTogglingId] = useState(null);
  const [sort, setSort] = useState({ col: 'name', dir: 'asc' });

  useEffect(() => {
    Promise.all([
      api.adminGetPaymentConfig(),
      api.adminGetBrackets(),
    ]).then(([cfg, brk]) => {
      setConfig({
        entryFee: cfg.entryFee ?? '',
        paypalUrl: cfg.paypalUrl || '',
        venmoUsername: cfg.venmoUsername || '',
        contactEmail: cfg.contactEmail || '',
        enabled: cfg.enabled || false,
        hideUnpaid: cfg.hideUnpaid || false,
      });
      setBrackets(Array.isArray(brk) ? brk : []);
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleSaveConfig() {
    setSaving(true); setError(''); setStatus('');
    try {
      await api.adminSavePaymentConfig(config);
      setStatus('Payment settings saved.');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function togglePaid(bracket) {
    setTogglingId(bracket.id);
    try {
      const newPaid = !bracket.paid;
      await api.adminSetBracketPaid(bracket.id, newPaid);
      setBrackets(prev => prev.map(b =>
        b.id === bracket.id
          ? { ...b, paid: newPaid, paidAt: newPaid ? new Date().toISOString() : null }
          : b
      ));
    } catch (e) { setError(e.message); }
    finally { setTogglingId(null); }
  }

  function toggleSort(col) {
    setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
  }
  const SortIcon = ({ col }) => (
    <span style={{ fontSize: '0.6rem', marginLeft: 3, opacity: sort.col === col ? 1 : 0.3 }}>
      {sort.col === col ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const filtered = brackets.filter(b => {
    const matchSearch = !search ||
      b.name?.toLowerCase().includes(search.toLowerCase()) ||
      b.userName?.toLowerCase().includes(search.toLowerCase()) ||
      b.userEmail?.toLowerCase().includes(search.toLowerCase());
    const matchPaid =
      filterPaid === 'all' ||
      (filterPaid === 'paid' && b.paid) ||
      (filterPaid === 'unpaid' && !b.paid);
    return matchSearch && matchPaid;
  }).sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    switch (sort.col) {
      case 'name': return dir * (a.name || '').localeCompare(b.name || '');
      case 'user': return dir * (a.userName || '').localeCompare(b.userName || '');
      case 'email': return dir * (a.userEmail || '').localeCompare(b.userEmail || '');
      case 'paid': return dir * ((a.paid ? 1 : 0) - (b.paid ? 1 : 0));
      case 'paidAt': return dir * (a.paidAt || '').localeCompare(b.paidAt || '');
      default: return 0;
    }
  });

  const paidCount = brackets.filter(b => b.paid).length;
  const unpaidCount = brackets.length - paidCount;
  const totalOwed = unpaidCount * (Number(config.entryFee) || 0);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray)' }}>Loading...</div>;

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--blue)', letterSpacing: '2px', marginBottom: 20 }}>
        PAYMENT SETTINGS
      </h2>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>
        {error}
        <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>&#x2715;</button>
      </div>}
      {status && <div className="alert alert-success" style={{ marginBottom: 16 }}>{status}</div>}

      {/* Config card */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: '1.1rem' }}>CONFIGURATION</h3>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            fontFamily: 'var(--font-condensed)', fontSize: '0.85rem',
            color: config.enabled ? 'var(--green)' : 'var(--gray)',
          }}>
            <input type="checkbox" checked={config.enabled}
              onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))} />
            {config.enabled ? 'Payments Enabled (visible to users)' : 'Payments Disabled (hidden from users)'}
          </label>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            fontFamily: 'var(--font-condensed)', fontSize: '0.85rem',
            color: config.hideUnpaid ? 'var(--orange-light)' : 'var(--gray)',
          }}>
            <input type="checkbox" checked={config.hideUnpaid}
              onChange={e => setConfig(c => ({ ...c, hideUnpaid: e.target.checked }))} />
            {config.hideUnpaid ? 'Hiding unpaid brackets from leaderboard' : 'Unpaid brackets visible on leaderboard'}
          </label>
        </div>

        <div className="grid-2" style={{ gap: 16, marginBottom: 20 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Entry Fee per Bracket ($)</label>
            <input className="form-input" type="number" min={0} step={1}
              value={config.entryFee}
              onChange={e => setConfig(c => ({ ...c, entryFee: e.target.value }))}
              placeholder="e.g. 25" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Contact Email (for physical payments)</label>
            <input className="form-input" type="email"
              value={config.contactEmail}
              onChange={e => setConfig(c => ({ ...c, contactEmail: e.target.value }))}
              placeholder="e.g. you@example.com" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">PayPal.me URL</label>
            <input className="form-input"
              value={config.paypalUrl}
              onChange={e => setConfig(c => ({ ...c, paypalUrl: e.target.value }))}
              placeholder="https://paypal.me/yourusername" />
            <div style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: 4 }}>
              Create one free at{' '}
              <a href="https://paypal.me" target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>paypal.me</a>
            </div>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Venmo Username (without @)</label>
            <input className="form-input"
              value={config.venmoUsername}
              onChange={e => setConfig(c => ({ ...c, venmoUsername: e.target.value.replace(/^@/, '') }))}
              placeholder="yourusername" />
            <div style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: 4 }}>
              Found in your Venmo app under Settings &rarr; Username
            </div>
          </div>
        </div>

        <button className="btn btn-primary" onClick={handleSaveConfig} disabled={saving}>
          {saving ? 'Saving...' : '\uD83D\uDCBE Save Settings'}
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid-3" style={{ marginBottom: 24, gap: 16 }}>
        <div className="stat-box" style={{ textAlign: 'center' }}>
          <div className="stat-value" style={{ color: 'var(--green)' }}>{paidCount}</div>
          <div className="stat-label">Paid</div>
        </div>
        <div className="stat-box" style={{ textAlign: 'center' }}>
          <div className="stat-value" style={{ color: '#e67e22' }}>{unpaidCount}</div>
          <div className="stat-label">Unpaid</div>
        </div>
        <div className="stat-box" style={{ textAlign: 'center' }}>
          <div className="stat-value">${totalOwed}</div>
          <div className="stat-label">Outstanding</div>
        </div>
      </div>

      {/* Brackets list */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: '1.1rem' }}>BRACKET PAYMENT STATUS</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="tabs" style={{ margin: 0 }}>
              {[
                { key: 'all', label: `All (${brackets.length})` },
                { key: 'unpaid', label: `Unpaid (${unpaidCount})` },
                { key: 'paid', label: `Paid (${paidCount})` },
              ].map(f => (
                <button key={f.key} className={`tab${filterPaid === f.key ? ' active' : ''}`}
                  onClick={() => setFilterPaid(f.key)}
                  style={{ fontSize: '0.75rem', padding: '4px 12px' }}>
                  {f.label}
                </button>
              ))}
            </div>
            <input className="form-input" type="text" placeholder="Search..."
              value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>Bracket Name <SortIcon col='name' /></th>
                <th onClick={() => toggleSort('user')} style={{ cursor: 'pointer' }}>User <SortIcon col='user' /></th>
                <th onClick={() => toggleSort('email')} style={{ cursor: 'pointer' }}>Email <SortIcon col='email' /></th>
                <th onClick={() => toggleSort('paid')} style={{ cursor: 'pointer', textAlign: 'center' }}>Status <SortIcon col='paid' /></th>
                <th onClick={() => toggleSort('paidAt')} style={{ cursor: 'pointer', textAlign: 'center' }}>Paid On <SortIcon col='paidAt' /></th>
                <th style={{ textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id}>
                  <td style={{ fontWeight: 600 }}>{b.name}</td>
                  <td style={{ color: 'var(--gray)' }}>{b.userName}</td>
                  <td style={{ color: 'var(--gray)', fontSize: '0.82rem' }}>{b.userEmail}</td>
                  <td style={{ textAlign: 'center' }}>
                    {b.paid
                      ? <span className="badge badge-green">&#10003; Paid</span>
                      : <span className="badge badge-orange">Unpaid</span>
                    }
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--gray)' }}>
                    {b.paidAt ? new Date(b.paidAt).toLocaleDateString() : '\u2014'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className={`btn btn-sm ${b.paid ? 'btn-secondary' : 'btn-primary'}`}
                      onClick={() => togglePaid(b)}
                      disabled={togglingId === b.id}
                      style={{ fontSize: '0.75rem', minWidth: 90 }}>
                      {togglingId === b.id ? '...' : b.paid ? 'Mark Unpaid' : 'Mark Paid'}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--gray)', padding: 32 }}>
                    No brackets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
